import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import * as fromSelectors from '@store/selectors/app.selectors';
import * as fromActions from '@store/actions/app.actions';

import { DirectivesModule } from '@directives/directives.module';
import { CsIconModule } from '@components/icon';
import { AppConfig } from 'client/app/app.config';
import { ProductCardGridComponent } from '../product-card-grid/product-card-grid.component';
import { CartService } from '@services/cart-service/cart.service';
import { ButtonModule } from '@components/button';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { BroadcastService } from '@services/broadcast-service';
import { AovBannerComponent } from 'client/app/cart/aov-banner/app-aov-banner.component';
import { AovSaleModule } from '@shared/aov-sale/aov-sale.module';
import { LoadingModule } from '@components/loading';
import { CartUpdateOptions, CartUpdateResult } from '@shared/types/cart.types';
import { RecentlyViewedProductsService } from '@services/recently-viewed-products/recently-viewed-products.service';
import { RecentlyViewedItem, RecentlyViewedConfig, RecentlyViewedLocation, RecentlyViewedEnum } from '@shared/types/recently-viewed.types';
import { PersonalisedPromotionsService } from '@services/personalised-promotions-service';

@Component({
  selector: 'recently-viewed-products',
  templateUrl: './recently-viewed-products.component.html',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    DirectivesModule,
    CsIconModule,
    ProductCardGridComponent,
    ButtonModule,
    AovBannerComponent,
    AovSaleModule,
    LoadingModule
  ],
})
export class RecentlyViewedProductsComponent implements OnInit, OnDestroy {
  @Input() location: RecentlyViewedLocation = RecentlyViewedEnum.Location.DEFAULT;
  @Input() isFullPage: boolean = false;
  @Input() isShopRevampExperimentOn: boolean = false;

  @Input() cartData: any = null;
  @Input() recentlyViewedData: RecentlyViewedItem[] = [];
  recentlyViewedProducts: RecentlyViewedItem[] = [];
  isLoading: boolean = false;
  shouldRenderComponent: boolean = false;
  isCartOperationPending: boolean = false;

  get gridSection(): string {
    return this.location === RecentlyViewedEnum.Location.HOME ? 'recentlyViewedHome' : 'recentlyViewedShop';
  }

  // Cart state
  cartProductQuantities: { [key: string]: number } = {};
  cartItems: any[] = [];
  cartToolbarIcon: any = {
    clickId: 'cart_page',
    name: this.appConfig.Toolbar.ICONS.CART,
    callback: (): void => this.navigateToCart(),
    cartValue: null,
  };

  aovConfiguration: any;
  aovFreeProduct: any;
  aovProductImage: any;
  aovProgressPercentage: number = 0;
  aovAmountRemaining: number = 0;
  aovProgressBarStyle: any = {};
  shouldShowConfettiAnimation: boolean = false;
  isAovExpEnabled: boolean = false;
  isAOVActive: boolean = false;

  totalSellingPrice: number = 0;
  totalMarketPrice: number = 0;
  discount: number = 0;

  currentUser: any;
  userExperiments: Array<any> = [];
  isNewCartUIEnabled: boolean = false;
  userCashBalance: number = 0;

  recentlyViewedConfig: RecentlyViewedConfig | null = null;
  showRemoveProductIcon: boolean = true;
  viewedProductToHide: RecentlyViewedItem;

  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    public readonly appConfig: AppConfig,
    private readonly cartService: CartService,
    private readonly connectionService: ConnectionService,
    private readonly eventLoggerService: EventLoggerService,
    private readonly store: Store,
    private readonly broadcastService: BroadcastService,
    private readonly recentlyViewedService: RecentlyViewedProductsService,
    private readonly personalisedPromotionsService: PersonalisedPromotionsService,
  ) {
    this.initializeFullPageMode();
  }

  ngOnInit(): void {
    this.initializeComponent();
    this.subscribeToCartChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  private initializeFullPageMode(): void {
    const isFullPageFromRoute = this.activatedRoute.snapshot.data['isFullPage'];
    if (isFullPageFromRoute) {
      this.isFullPage = true;
    }
  }

  private async initializeComponent(): Promise<void> {
    try {
      this.currentUser = this.connectionService.getActingUser();
      this.userCashBalance = await this.connectionService.fetchCashBalance();
      this.userExperiments = await this.connectionService.findUserActiveExperiments();
      await this.processUserExperiments();
      await this.fetchRecentlyViewedData();
      if (!this.shouldRenderComponent) return;
      if (this.cartData && !this.isFullPage) {
        this.useProvidedCartData(this.cartData);
      } else {
        await this.loadCartData();
      }
    } catch (error) {
      this.shouldRenderComponent = false;
      this.isLoading = false;
      this.resetCartState();
    }
  }

  private async fetchRecentlyViewedData(): Promise<void> {
    if (this.recentlyViewedData && this.recentlyViewedData.length > 0) {
      this.useProvidedRecentlyViewedData();
    } else {
      await this.loadRecentlyViewedDataViaService();
    }
  }

  private useProvidedRecentlyViewedData(): void {
    this.recentlyViewedProducts = this.recentlyViewedData;
    this.recentlyViewedConfig = this.recentlyViewedService.getDefaultConfig();
    this.shouldRenderComponent = true;
    this.isLoading = false;
  }

  private async loadRecentlyViewedDataViaService(): Promise<void> {
    this.isLoading = true;

    try {
      const data = await this.recentlyViewedService.getRecentlyViewedData(
        this.location,
        this.isFullPage
      );

      if (data) {
        this.recentlyViewedProducts = data.items;
        this.shouldRenderComponent = data.shouldShowComponent;
        this.recentlyViewedConfig = data.config;
      } else {
        this.shouldRenderComponent = false;
        this.recentlyViewedProducts = [];
      }
    } catch (error) {
      this.shouldRenderComponent = false;
      this.recentlyViewedProducts = [];
    } finally {
      this.isLoading = false;
      this.validateAndNavigateToShop();
    }
  }

  private validateAndNavigateToShop(): void {
    if (this.recentlyViewedProducts.length === 0 && this.isFullPage) {
      this.router.navigate(['/user'], { queryParams: { tab: 'shop', back: 'home' } });
    }
  }

  private async processUserExperiments(): Promise<void> {
    this.isAOVActive = await this.personalisedPromotionsService.isAOVActiveInPersonalisedPromotions(this.currentUser);
    this.userExperiments.forEach(experiment => {
      if (experiment.key === 'aov_gamification' && this.isAOVActive) {
        this.setAovGamificationData(experiment);
      }
      if (experiment.key === 'new_cart_redesign') {
        this.isNewCartUIEnabled = true;
      }
      if (experiment.key === 'shop_revamp' && this.isFullPage) {
        this.isShopRevampExperimentOn = true;
      }
    });
  }

  private async setAovGamificationData(experiment: any): Promise<void> {
    this.aovConfiguration = experiment.variant;
    this.isAovExpEnabled = experiment.enable;

    const freeProduct = await this.connectionService.findCatalogWithKey([this.aovConfiguration.productID]);
    [this.aovFreeProduct] = JSON.parse(JSON.stringify(freeProduct));

    this.aovProductImage = this.selectAovProductImage();
  }

  private selectAovProductImage(): string {
    const product = this.aovFreeProduct;
    const config = this.aovConfiguration;

    return product?.rebrandedImageWithoutBackground?.[0] ||
           config?.productImage ||
           product?.rebrandedImageWithBackground?.[0] ||
           product?.productUnboxedImage?.[0] ||
           '';
  }

  private useProvidedCartData(cartData: any): void {
    this.updateCartState(cartData);
    this.updateCartToolbarIcon();
  }

  private async loadCartData(): Promise<void> {
    try {
      const aovOptions = this.isFullPage && this.isAovExpEnabled
        ? { aovTask: true, maxPrice: this.aovConfiguration.totalValue }
        : { aovTask: false, maxPrice: 0 };
      const cartData = await this.cartService.getCartAndPricingDetails(this.userCashBalance, aovOptions);

      this.updateCartState(cartData);
      this.updateCartToolbarIcon();

      if (this.isFullPage && this.isAovExpEnabled && cartData.freeProductResult) {
        this.handleAovResult(cartData.freeProductResult);
      }
    } catch (error) {
      this.resetCartState();
    }
  }

  private updateCartState(cartData: any): void {
    this.totalSellingPrice = cartData.totalSP ?? 0;
    this.totalMarketPrice = cartData.totalMRP ?? 0;
    this.discount = cartData.discountPercentage ?? 0;
    this.cartItems = cartData.cartItems ?? [];

    this.cartProductQuantities = this.buildCartProductQuantities();
  }

  private buildCartProductQuantities(): { [key: string]: number } {
    const quantities: { [key: string]: number } = {};
    this.cartItems.forEach((item: any) => {
      quantities[item.productId] = item.quantity;
    });
    return quantities;
  }

  private updateCartToolbarIcon(): void {
    if (this.cartItems?.length > 0) {
      this.cartToolbarIcon.cart = true;
      this.cartToolbarIcon.cartValue = this.cartItems.reduce((total, item) => total + item.quantity, 0);
    } else {
      this.cartToolbarIcon.cart = false;
      this.cartToolbarIcon.cartValue = null;
    }
  }

  private handleAovResult(freeProductResult: any): void {
    if (freeProductResult.shouldShowConfetti) {
      this.triggerConfettiAnimation();
    }

    this.calculateAovProgress();
    this.updateAovProgressBarStyle();
  }

  private triggerConfettiAnimation(): void {
    this.shouldShowConfettiAnimation = true;
    setTimeout(() => {
      this.shouldShowConfettiAnimation = false;
    }, 2000);
  }

  private calculateAovProgress(): void {
    const maxPrice = this.aovConfiguration.totalValue;
    this.aovProgressPercentage = Math.round((this.totalSellingPrice / maxPrice) * 100);
    this.aovAmountRemaining = Math.max(0, maxPrice - this.totalSellingPrice);
  }

  private updateAovProgressBarStyle(): void {
    const isTotalValueReached = this.totalSellingPrice >= this.aovConfiguration.totalValue;

    this.aovProgressBarStyle = {
      width: isTotalValueReached ? '100%' : `${this.aovProgressPercentage}%`,
      backgroundColor: isTotalValueReached ? '#5EAC73' : '#D66736',
      amountRemaining: this.aovAmountRemaining
    };
  }

  private resetCartState(): void {
    this.cartProductQuantities = {};
    this.cartItems = [];
  }

  private subscribeToCartChanges(): void {
    // Subscribe to cart products from Redux store for both views
    const cartProductsSubscription = this.store.select(fromSelectors.selectCartProducts).subscribe((products: any) => {
      this.cartProductQuantities = { ...products };
    });
    const refreshCartSubscription = this.broadcastService.on('REFRESH_SHOP_CART').subscribe((payload: { productsInCart?: { [key: string]: number } } | undefined) => {
      if (payload?.productsInCart) {
        this.store.dispatch(fromActions.CartUpdateProductsBegin({ products: payload.productsInCart }));
      }
      this.loadCartData();
    });
    this.subscriptions.push(
      cartProductsSubscription,
      refreshCartSubscription
    );
  }

  async onProductClick(product: any): Promise<void> {
    const guardResult = await this.cartService.checkProductAllowedForCartAndPrompt(
      product,
      {
        productsInCart: this.cartProductQuantities,
        navigateToArticlePage: true,
        skipDiscontinuedCheck: true
      });
    if (!guardResult?.allowed) {
      return;
    }
    const productId = product.productId || product.objectId;
    this.connectionService.navigateToURL(`/product/${productId}`);
  }

  async onSucessfulCartUpdate(event: CartUpdateResult): Promise<void> {
    if (!event.success) return;
    await this.refreshCartData();
  }

  async onAddToCartClick(product: any, action: string, event: Event, imageElement?: HTMLImageElement, tag?: string): Promise<void> {
    event.stopPropagation();

    // Prevent rapid clicks - only allow one operation at a time
    if (this.isCartOperationPending) {
      return;
    }

    this.isCartOperationPending = true;

    try {
      const cartUpdateOperations = this.generateCartUpdateOptions(imageElement, tag);
      const result = await this.cartService.addOrRemoveFromCart(product, action, cartUpdateOperations);

      if (result.success) {
        // Update local state first
        this.updateLocalCartState(product, result);

        // Sync with Redux store (this will notify other components)
        this.store.dispatch(fromActions.CartUpdateProductsBegin({
          products: this.cartProductQuantities
        }));

        // Update local UI
        this.updateCartToolbarIcon();

        await this.onSucessfulCartUpdate(result);
      }
    } finally {
      this.isCartOperationPending = false;
    }
  }

  getProductQuantityInCart(recentView: any): number {
    const productId = recentView?.product?.objectId;
    return this.cartProductQuantities[productId] ?? 0;
  }

  getProductImage(product: any): string {
    return product.rebrandedImageWithBackground ||
           product.rebrandedImage ||
           product.rebrandedImageWithoutBackground ||
           product.imageURL ||
           '';
  }


  navigateToRecentlyViewedPage(): void {
    const eventName = this.location === RecentlyViewedEnum.Location.HOME ? 'HP_recently_viewed' : 'SP_recently_viewed';
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: eventName }));
    this.router.navigate(['/user/recently-viewed']);
  }

  navigateBack(): void {
    this.broadcastService.broadcast('NAVIGATION_BACK');
  }

  navigateToCart(): void {
    this.router.navigate(['/cart']);
  }

  navigateToCheckout(): void {
    this.router.navigate(['cart']);
  }

  private generateCartUpdateOptions(imageElement?: HTMLImageElement, tag?: string): CartUpdateOptions {
    const eventName = this.location === RecentlyViewedEnum.Location.HOME ? 'HP_recently_viewed_cart_update' : 'SP_recently_viewed_cart_update';
    const section = this.location === RecentlyViewedEnum.Location.HOME ? 'recentlyViewedHome' : 'recentlyViewedShop';
    return {
      img: imageElement,
      productsInCart: this.cartProductQuantities,
      cartItems: this.cartItems,
      tag,
      eventName,
      section,
    };
  }

  private updateLocalCartState(product: any, result: CartUpdateResult): void {
    const productId = product.objectId;
    if (result.updatedQuantity !== undefined) {
      if (result.updatedQuantity > 0) {
        this.cartProductQuantities[productId] = result.updatedQuantity;
      } else {
        delete this.cartProductQuantities[productId];
      }
    }
  }

  private async refreshCartData(): Promise<void> {
    if (this.isFullPage) {
      // For full page view, refresh cart data directly
      await this.loadCartData();
    } else {
      // For embedded view, notify shop component to refresh its cart data and AOV calculations
      this.broadcastService.broadcast('REFRESH_SHOP_CART');
    }
  }

  get products(): Array<any> {
    return this.recentlyViewedProducts.map(recentView => recentView.product);
  }

  async refreshData(): Promise<void> {
    await this.loadRecentlyViewedDataViaService();
  }

  getProductTag(recentView: any): { text: string; show: boolean; type: string } {
    return this.recentlyViewedService.getProductTag(recentView, this.recentlyViewedConfig);
  }

  async onRemoveProductClick(product: any): Promise<void> {
    if (!product) return;
    this.viewedProductToHide = this.recentlyViewedProducts.find(recentView => recentView.product.objectId === product.objectId);
    if (!this.viewedProductToHide) return;
    let result:RecentlyViewedItem;
    try {
      result = await this.connectionService.updateViewedProductStatus(product.objectId, false);
    } catch (error) {
      this.eventLoggerService.trackEvent('recently_viewed_status_update_error', {
        productId: product.objectId,
        error: error.message,
        errorStack: error.stack
      });
      return;
    }
    if (result && !result.isActive) {
      this.recentlyViewedProducts = this.recentlyViewedProducts.filter(recentView => recentView.product.objectId !== product.objectId);
    }

    if (this.recentlyViewedProducts.length === 0 && this.isFullPage) {
      this.router.navigate(['/user'], { queryParams: { tab: 'shop', back: 'home' } });
    }
  }
}
